import React, { useState, useEffect } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const ROLE_KEYS = ['manager', 'assistant manager', 'server', 'cook', 'host', 'dishwasher'];
const AVAILABILITY_OPTIONS = ['all', 'available', 'scheduled', 'unavailable', 'off'];

const EmployeeSidebar = ({ 
  employees, 
  onEmployeeSelect, 
  selectedEmployee,
  isCollapsed,
  onToggle,
  filters,
  onFiltersChange
}) => {
  const { t } = useTranslation(['employeeScheduling']);
  const [searchTerm, setSearchTerm] = useState('');
  const normalizeRoleKey = (value) => value?.toString()?.toLowerCase?.().trim?.();
  const getEmployeeAlt = (name) =>
    name
      ? t('employeeScheduling.sidebar.employeePhotoAlt', { name })
      : t('employeeScheduling.sidebar.employeePhotoAlt', {
        name: t('employeeScheduling.sidebar.defaultEmployeeName'),
      });

  const [selectedStaffMembers, setSelectedStaffMembers] = useState(
    employees?.reduce((acc, emp) => ({ ...acc, [emp?.id]: true }), {}) || {}
  );
  const [selectedRoles, setSelectedRoles] = useState(
    ROLE_KEYS?.reduce((acc, role) => ({ ...acc, [role]: true }), {}) || {},
  );
  const [availabilityFilter, setAvailabilityFilter] = useState('all');

  // Update parent component with current filters
  React.useEffect(() => {
    onFiltersChange?.({
      selectedStaffMembers,
      selectedRoles,
      searchTerm,
      availabilityFilter
    });
  }, [selectedStaffMembers, selectedRoles, searchTerm, availabilityFilter, onFiltersChange]);

  const filteredEmployees = employees?.filter(employee => {
    const matchesSearch = employee?.name?.toLowerCase?.()?.includes(searchTerm?.toLowerCase()) ||
                         employee?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStaff = selectedStaffMembers?.[employee?.id];
    const roleKey = normalizeRoleKey(employee?.role);
    const matchesRole = roleKey ? selectedRoles?.[roleKey] !== false : true;
    const matchesAvailability = availabilityFilter === 'all' || employee?.availability === availabilityFilter;
    
    return matchesSearch && matchesStaff && matchesRole && matchesAvailability;
  });

  const handleStaffMemberToggle = (employeeId) => {
    setSelectedStaffMembers(prev => ({
      ...prev,
      [employeeId]: !prev?.[employeeId]
    }));
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles(prev => ({
      ...prev,
      [role]: !prev?.[role]
    }));
  };

  const handleToggleAllStaff = () => {
    const allSelected = employees?.every(emp => selectedStaffMembers?.[emp?.id]);
    const newState = employees?.reduce((acc, emp) => ({ ...acc, [emp?.id]: !allSelected }), {});
    setSelectedStaffMembers(newState);
  };

  const handleToggleAllRoles = () => {
    const allSelected = ROLE_KEYS?.every((role) => selectedRoles?.[role] !== false);
    const newState = ROLE_KEYS?.reduce((acc, role) => ({ ...acc, [role]: !allSelected }), {});
    setSelectedRoles(newState);
  };

  const getAvailabilityColor = (status) => {
    const colors = {
      'available': 'text-success',
      'scheduled': 'text-warning',
      'unavailable': 'text-error',
      'off': 'text-muted-foreground'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const getAvailabilityIcon = (status) => {
    const icons = {
      'available': 'CheckCircle',
      'scheduled': 'Clock',
      'unavailable': 'XCircle',
      'off': 'Minus'
    };
    return icons?.[status] || 'Minus';
  };

  useEffect(() => {
    setSelectedStaffMembers((prev) => (
      employees?.reduce((acc, emp) => ({
        ...acc,
        [emp?.id]: prev?.[emp?.id] ?? true,
      }), {}) || {}
    ));
  }, [employees]);

  if (isCollapsed) {
    return (
      <div className="w-12 bg-card border-r border-border flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="mb-4"
        >
          <Icon name="ChevronRight" size={20} />
        </Button>
        <div className="flex flex-col space-y-2">
          {filteredEmployees?.slice(0, 8)?.map((employee) => (
            <div
              key={employee?.id}
              className="w-8 h-8 rounded-full overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              onClick={() => onEmployeeSelect(employee)}
              title={employee?.name}
            >
              <EmployeeAvatar
                url={employee?.avatarUrl}
                name={employee?.name}
                alt={getEmployeeAlt(employee?.name)}
                status={employee?.status}
                size="xs"
                showStatus={false}
              />
            </div>
          ))}
          {filteredEmployees?.length > 8 && (
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
              +{filteredEmployees?.length - 8}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-card border-r border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t('employeeScheduling.sidebar.title')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
          >
            <Icon name="ChevronLeft" size={20} />
          </Button>
        </div>

        {/* Search */}
        <Input
          type="search"
          placeholder={t('employeeScheduling.sidebar.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="mb-3"
        />

        {/* Availability Filter */}
        <div className="mb-4">
          <label className="text-sm font-medium text-foreground mb-1 block">
            {t('employeeScheduling.sidebar.availabilityLabel')}
          </label>
          <select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {AVAILABILITY_OPTIONS?.map((option) => (
              <option key={option} value={option}>
                {t(`employeeScheduling.sidebar.availabilityOptions.${option}`, option)}
              </option>
            ))}
          </select>
        </div>

        {/* Role Filters */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              {t('employeeScheduling.sidebar.rolesLabel')}
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleAllRoles}
              className="text-xs h-6 px-2"
            >
              {ROLE_KEYS?.every((role) => selectedRoles?.[role] !== false)
                ? t('employeeScheduling.sidebar.unselectAll')
                : t('employeeScheduling.sidebar.selectAll')}
            </Button>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {ROLE_KEYS?.map((role) => (
              <label key={role} className="flex items-center space-x-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selectedRoles?.[role] !== false}
                  onChange={() => handleRoleToggle(role)}
                />
                <span className="capitalize flex-1">
                  {t(`employeeScheduling.roles.${role}`, role)}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({employees?.filter(emp => normalizeRoleKey(emp?.role) === role)?.length})
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Staff Member Filters */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              {t('employeeScheduling.sidebar.staffLabel')}
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleAllStaff}
              className="text-xs h-6 px-2"
            >
              {employees?.every((emp) => selectedStaffMembers?.[emp?.id])
                ? t('employeeScheduling.sidebar.unselectAll')
                : t('employeeScheduling.sidebar.selectAll')}
            </Button>
          </div>
        </div>
      </div>

      {/* Employee List with Checkboxes */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2">
          {employees?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">{t('employeeScheduling.sidebar.none')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {employees?.map((employee) => (
                <div
                  key={employee?.id}
                  className={`p-3 rounded-lg border transition-all hover:shadow-sm ${
                    selectedEmployee?.id === employee?.id
                      ? 'border-primary bg-primary/5' 
                      : selectedStaffMembers?.[employee?.id] 
                        ? 'border-border hover:border-primary/50' :'border-border bg-muted/20 opacity-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={selectedStaffMembers?.[employee?.id] || false}
                      onChange={() => handleStaffMemberToggle(employee?.id)}
                      className="mt-1"
                    />
                    
                    <div
                      className="flex-shrink-0 cursor-pointer"
                      onClick={() => onEmployeeSelect(employee)}
                    >
                      <EmployeeAvatar
                        url={employee?.avatarUrl}
                        name={employee?.name}
                        alt={getEmployeeAlt(employee?.name)}
                        status={employee?.status}
                        size="sm"
                        showStatus={false}
                      />
                    </div>
                    
                    <div 
                      className="flex-1 min-w-0 cursor-pointer" 
                      onClick={() => onEmployeeSelect(employee)}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-foreground truncate">
                          {employee?.name}
                        </h4>
                        <Icon
                          name={getAvailabilityIcon(employee?.availability)}
                          size={14}
                          className={getAvailabilityColor(employee?.availability)}
                        />
                      </div>
                      
                      <p className="text-xs text-muted-foreground capitalize mb-1">
                        {employee?.role}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className={`capitalize ${getAvailabilityColor(employee?.availability)}`}>
                          {t(
                            `employeeScheduling.sidebar.availabilityOptions.${employee?.availability ?? 'all'}`,
                            employee?.availability,
                          )}
                        </span>
                        <span className="text-muted-foreground">
                          {formatTranslation(t('employeeScheduling.sidebar.hoursThisWeek'), {
                            hours: employee?.hoursThisWeek ?? 0,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer Stats */}
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-foreground">
              {filteredEmployees?.filter(e => e?.availability === 'available')?.length}
            </div>
            <div className="text-xs text-muted-foreground">Available</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-foreground">
              {filteredEmployees?.filter(e => e?.availability === 'scheduled')?.length}
            </div>
            <div className="text-xs text-muted-foreground">Scheduled</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <div className="text-sm text-muted-foreground">
            {formatTranslation(t('employeeScheduling.sidebar.footer.showing'), {
              visible: filteredEmployees?.length ?? 0,
              total: employees?.length ?? 0,
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeSidebar;
